import { useEffect, useRef, useState } from 'react';
import * as RadixDialog from '@radix-ui/react-dialog';
import {
  animate,
  motion,
  useDragControls,
  useMotionValue,
  useReducedMotion,
  type PanInfo,
} from 'motion/react';
import { Plus, Sparkles, X } from 'lucide-react';
import { useUIStore } from '@/stores/uiStore';
import { useCommand } from './useCommand';
import { AskThread } from './AskThread';
import { AskComposer } from './AskComposer';
import { dispatch as dispatchAction } from './actionDispatcher';
import { hapticSelection, hapticTap } from '@/features/lifecycle/haptics';

// One tall sheet — the composer is always visible (a half-height "peek" detent
// hid it, which read as "no input"). Drag the grip DOWN to dismiss.
const SHEET_VH = 0.9;
const OPEN_SPRING = { type: 'spring', bounce: 0.12, duration: 0.34 } as const;

export function AskSheet() {
  const open = useUIStore((s) => s.commandPaletteOpen);
  const setOpen = useUIStore((s) => s.setCommandPaletteOpen);
  const {
    messages,
    streamingText,
    loading,
    error,
    run,
    newChat,
    outcomes,
    markOutcome,
    clearOutcome,
  } = useCommand();
  const [input, setInput] = useState('');
  const reduce = useReducedMotion();
  const dragControls = useDragControls();
  const dismissingRef = useRef(false);

  // Sheet height in px (90vh). `y` translates it: 0 = open, sheetH = off-screen.
  const [sheetH, setSheetH] = useState(0);
  useEffect(() => {
    const compute = () =>
      setSheetH(Math.round((typeof window === 'undefined' ? 800 : window.innerHeight) * SHEET_VH));
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, []);

  const y = useMotionValue(0);

  // Slide up from the bottom on open.
  useEffect(() => {
    if (!open) return;
    dismissingRef.current = false;
    y.set(sheetH || 1000);
    if (reduce) y.set(0);
    else void animate(y, 0, OPEN_SPRING);
  }, [open, sheetH, reduce, y]);

  // Consume a draft handed off from the capture composer's "Ask instead" —
  // the text the user already typed lands in the composer, once. The setState
  // is deferred a microtask so the open commit finishes first (no cascading
  // sync render inside the effect).
  useEffect(() => {
    if (!open) return;
    const ui = useUIStore.getState();
    const seed = ui.askSeed;
    if (seed === null) return;
    ui.setAskSeed(null);
    queueMicrotask(() => setInput(seed));
  }, [open]);

  const dismiss = () => {
    if (dismissingRef.current) return;
    dismissingRef.current = true;
    if (reduce) {
      setOpen(false);
      return;
    }
    void animate(y, (sheetH || 1000) + 40, { type: 'spring', bounce: 0, duration: 0.24 });
    window.setTimeout(() => setOpen(false), 240);
  };

  const onDragEnd = (_e: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    if (y.get() > (sheetH || 1000) * 0.28 || info.velocity.y > 700) {
      dismiss();
      return;
    }
    void animate(y, 0, OPEN_SPRING);
  };

  const submit = () => {
    const text = input.trim();
    if (!text || loading) return;
    void hapticTap();
    void run(text);
    setInput('');
  };

  return (
    <RadixDialog.Root open={open} onOpenChange={(next) => (next ? setOpen(true) : dismiss())}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-overlay bg-scrim ease-out-strong data-[state=open]:duration-modal data-[state=closed]:duration-modal data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 motion-reduce:animate-none" />
        <RadixDialog.Content asChild aria-describedby={undefined}>
          <motion.div
            style={{ height: sheetH || '90dvh', y }}
            drag="y"
            dragListener={false}
            dragControls={dragControls}
            dragConstraints={{ top: 0, bottom: (sheetH || 1000) + 60 }}
            dragElastic={{ top: 0, bottom: 0.2 }}
            onDragEnd={onDragEnd}
            className="fixed inset-x-0 bottom-0 z-overlay flex flex-col rounded-t-2xl border-t border-line-2 bg-canvas shadow-window outline-none"
          >
            <RadixDialog.Title className="sr-only">Ask Brainday</RadixDialog.Title>

            {/* Grip + header start the drag; the thread + composer below stay interactive. */}
            <div onPointerDown={(e) => dragControls.start(e)} style={{ touchAction: 'none' }}>
              <div className="flex justify-center pb-1 pt-2" aria-hidden>
                <div className="h-1 w-10 rounded-full bg-line-3" />
              </div>
              <header className="flex items-center gap-2 border-b border-line-2 px-4 pb-3">
                <Sparkles className="h-4 w-4 text-brand" aria-hidden />
                <span className="text-body-xl font-medium text-t1">Ask Brainday</span>
                <div className="ml-auto flex items-center gap-1">
                  <button
                    type="button"
                    aria-label="New chat"
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={() => {
                      void hapticSelection();
                      newChat();
                      setInput('');
                    }}
                    className="flex h-9 w-9 items-center justify-center rounded-md text-t3 outline-none can-hover:hover:bg-rail can-hover:hover:text-t1 focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-canvas pointer-coarse:min-h-[44px] pointer-coarse:min-w-[44px]"
                  >
                    <Plus className="h-5 w-5" aria-hidden />
                  </button>
                  <button
                    type="button"
                    aria-label="Close"
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={() => dismiss()}
                    className="flex h-9 w-9 items-center justify-center rounded-md text-t3 outline-none can-hover:hover:bg-rail can-hover:hover:text-t1 focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-canvas pointer-coarse:min-h-[44px] pointer-coarse:min-w-[44px]"
                  >
                    <X className="h-5 w-5" aria-hidden />
                  </button>
                </div>
              </header>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-3">
              <AskThread
                messages={messages}
                streamingText={streamingText}
                loading={loading}
                error={error}
                initialOutcomes={outcomes}
                onMarkOutcome={markOutcome}
                onClearOutcome={clearOutcome}
                onAction={(a) => {
                  void hapticSelection();
                  return dispatchAction(a, { silent: true });
                }}
                onSuggestion={(t) => {
                  setInput('');
                  void hapticTap();
                  void run(t);
                }}
              />
            </div>

            {/* No --keyboard-height padding: the WebView shrinks above the
                keyboard (Keyboard resize: 'native'), which already lifts the
                focused composer (same as the app's other forms). Adding it too
                double-compensated and pushed it way up. */}
            <div className="border-t border-line-2 px-3 pt-2 pb-[calc(env(safe-area-inset-bottom)+8px)]">
              <AskComposer
                value={input}
                onChange={setInput}
                onSubmit={submit}
                placeholder={messages.length ? 'Ask a follow-up…' : 'Ask Brainday…'}
                disabled={loading}
                showSend
              />
            </div>
          </motion.div>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
